import { BatteryCharging, BedDouble, Check, Coins, Copy, Receipt, UtensilsCrossed, Zap } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const TOLL_RE = /(tol(?![a-z])|toll|p[ée]age|vignet|maut)/i;
const CORE_CATS = ["laden", "verblijf", "eten"];
const isToll = (e) => e.category === "tol" || (!CORE_CATS.includes(e.category) && TOLL_RE.test(e.label || ""));
const eur = (n, dec = 2) => (n ?? 0).toLocaleString("nl-BE", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const buildRecap = (trip, progress) => {
  const budget = trip.budget;
  if (!budget?.per_day?.length) return null;
  const extras = trip.extra_costs || [];
  const byDay = {};
  for (const e of extras) {
    if (e.day_number == null || !CORE_CATS.includes(e.category)) continue;
    byDay[e.day_number] = byDay[e.day_number] || {};
    byDay[e.day_number][e.category] = (byDay[e.day_number][e.category] || 0) + (e.amount_eur || 0);
  }
  const cat = (slug, plannedOf) => {
    let total = 0, actualDays = 0, days = 0;
    for (const d of budget.per_day) {
      const s = (byDay[d.day_number] || {})[slug] || 0;
      const planned = plannedOf(d);
      if (!planned && s <= 0) continue;
      days += 1;
      if (s > 0) { total += s; actualDays += 1; } else total += planned;
    }
    const dayless = extras.filter((e) => e.day_number == null && e.category === slug).reduce((a, e) => a + (e.amount_eur || 0), 0);
    if (dayless > 0) { total += dayless; actualDays = Math.max(actualDays, 1); }
    return { total, actualDays, days };
  };
  const charging = cat("laden", (d) => (d.charging || 0) + (d.overnight_charging || 0));
  const stay = cat("verblijf", (d) => d.stay || 0);
  const food = cat("eten", (d) => d.food || 0);
  const tollActual = extras.filter(isToll).reduce((a, e) => a + (e.amount_eur || 0), 0);
  const tolls = { total: tollActual > 0 ? tollActual : (budget.tolls_total || 0), actual: tollActual > 0 };
  const other = extras.filter((e) => !CORE_CATS.includes(e.category) && !isToll(e)).reduce((a, e) => a + (e.amount_eur || 0), 0);
  const total = charging.total + stay.total + food.total + tolls.total + other;
  const km = progress?.total_km || trip.route?.distance_km || 0;
  const days = budget.per_day.length;
  const persons = (trip.travel_party?.adults || 2) + (trip.travel_party?.children || 0);
  const kwh = extras.reduce((a, e) => a + (e.kwh || 0), 0);
  return { charging, stay, food, tolls, other, total, km, days, persons, kwh };
};

const basisLabel = (c) => (c.actualDays === 0 ? "raming" : c.actualDays >= c.days ? "werkelijk" : `${c.actualDays}/${c.days} dagen werkelijk`);

const RecapRow = ({ icon: Icon, color, label, amount, basis, testId }) => (
  <div data-testid={testId} className="flex items-center justify-between py-2 text-sm">
    <span className="flex items-center gap-2 font-semibold"><Icon className={`w-4 h-4 ${color}`} /> {label}</span>
    <span className="text-right">
      <span className="font-mono font-bold">€ {eur(amount)}</span>
      <span className="block text-[10px] text-muted-foreground">{basis}</span>
    </span>
  </div>
);

export const CostRecap = ({ trip, progress }) => {
  const [copied, setCopied] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const finished = progress?.finished || (trip.end_date && trip.end_date < today);
  const r = finished ? buildRecap(trip, progress) : null;
  if (!r || !r.total) return null;
  const perKm = r.km > 0 ? r.charging.total / r.km : null;
  const totalPerKm = r.km > 0 ? r.total / r.km : null;
  const ppd = r.total / Math.max(r.persons, 1) / Math.max(r.days, 1);

  const shareText = () => {
    let t = `⚡ Kosten-recap ${trip.name}\n`;
    t += `${Math.round(r.km).toLocaleString("nl-BE")} km elektrisch in ${r.days} dagen\n`;
    t += `Laden € ${eur(r.charging.total)}${perKm != null ? ` (€ ${eur(perKm, 3)}/km!)` : ""} · verblijf € ${eur(r.stay.total)} · eten € ${eur(r.food.total)}`;
    if (r.tolls.total > 0) t += ` · tol € ${eur(r.tolls.total)}`;
    if (r.other > 0) t += ` · extra € ${eur(r.other)}`;
    t += `\nTotaal: € ${eur(r.total)} (€ ${eur(ppd)} p.p. per dag)\nGepland met VoltRoute ⚡`;
    return t;
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText());
      setCopied(true);
      toast.success("Kosten-recap gekopieerd!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Kopiëren mislukte");
    }
  };

  const stats = [
    perKm != null && { label: "laadkosten per km", value: `€ ${eur(perKm, 3)}` },
    totalPerKm != null && { label: "totaal per km", value: `€ ${eur(totalPerKm)}` },
    { label: "p.p. per dag", value: `€ ${eur(ppd)}` },
    r.kwh > 0 && { label: "kWh geladen", value: `${eur(r.kwh, 0)} kWh` },
  ].filter(Boolean);

  return (
    <div data-testid="cost-recap-card" className="bg-card rounded-2xl p-5 mb-4">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Kosten-recap</p>
          <p data-testid="recap-total" className="font-mono font-extrabold text-3xl mt-0.5">€ {eur(r.total)}</p>
          <p className="text-xs text-muted-foreground">{Math.round(r.km).toLocaleString("nl-BE")} km · {r.days} dagen · {r.persons} personen</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button data-testid="recap-copy" onClick={copy} title="Kopieer de recap als tekst"
            className="p-2.5 rounded-full bg-secondary hover:bg-secondary/70 text-foreground transition-[background-color] duration-200">
            {copied ? <Check className="w-4 h-4 text-[#2D6A4F]" /> : <Copy className="w-4 h-4" />}
          </button>
          <button data-testid="recap-share-whatsapp" title="Deel via WhatsApp"
            onClick={() => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText())}`, "_blank", "noopener")}
            className="p-2.5 rounded-full text-white hover:opacity-90 transition-opacity duration-200" style={{ backgroundColor: "#25D366" }}>
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.668-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 my-3">
        {stats.map((s) => (
          <span key={s.label} data-testid={`recap-stat-${s.label.replace(/[^a-z]+/gi, "-")}`}
            className="bg-secondary/60 rounded-full px-3 py-1.5 text-xs font-semibold">
            <span className="font-mono font-bold text-[#2D6A4F]">{s.value}</span> <span className="text-muted-foreground">{s.label}</span>
          </span>
        ))}
      </div>
      <div className="divide-y divide-border/60">
        <RecapRow testId="recap-cat-charging" icon={Zap} color="text-[#2D6A4F]" label="Laden" amount={r.charging.total} basis={basisLabel(r.charging)} />
        <RecapRow testId="recap-cat-stay" icon={BedDouble} color="text-[#3A6EA5]" label="Verblijf" amount={r.stay.total} basis={basisLabel(r.stay)} />
        <RecapRow testId="recap-cat-food" icon={UtensilsCrossed} color="text-[#E07A5F]" label="Eten & drinken" amount={r.food.total} basis={basisLabel(r.food)} />
        {r.tolls.total > 0 && <RecapRow testId="recap-cat-tolls" icon={Coins} color="text-[#B5838D]" label="Tol / péage" amount={r.tolls.total} basis={r.tolls.actual ? "werkelijk" : "raming"} />}
        {r.other > 0 && <RecapRow testId="recap-cat-other" icon={Receipt} color="text-muted-foreground" label="Overige extra's" amount={r.other} basis="werkelijk" />}
      </div>
      <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1.5">
        <BatteryCharging className="w-3 h-3" /> Werkelijke kosten waar ingevuld, anders de raming — handig als startpunt voor je volgende trip. Alleen zichtbaar voor jou en je reisgenoten.
      </p>
    </div>
  );
};
